import React, { useState, useEffect, useRef } from 'react';
import { Thumbnail } from '../Thumbnail';
import { useThumbnailLocationListener } from '@state';
import { useViewerLayoutContext } from '../../contextProviders';
import { useDynamicMaxHeight } from '../../hooks/useDynamicMaxHeight';
import { ScrollArea } from '../ScrollArea';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';

function normalizeNumInstances(numInstances): number {
  const normalized = Number(numInstances);

  return Number.isFinite(normalized) && normalized > 0 ? normalized : 1;
}

const ThumbnailList = ({
  servicesManager,
  thumbnails,
  onThumbnailClick,
  onThumbnailDoubleClick,
  onClickUntrack,
  activeDisplaySetInstanceUIDs = [],
  viewPreset,
  ThumbnailMenuItems,
}) => {
  // Use the dynamic height hook on the parent container
  const { ref, maxHeight } = useDynamicMaxHeight(thumbnails);
  const scrollContainerRef_thumnailList = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(true);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);

  // Filter thumbnails into list items and thumbnail items
  const listItems = thumbnails?.filter(
    ({ componentType }) => componentType === 'thumbnailNoImage' || viewPreset === 'list'
  );

  const thumbnailItems = thumbnails?.filter(
    ({ componentType }) => componentType !== 'thumbnailNoImage' && viewPreset === 'thumbnails'
  );

  const thumbnailLocation = useThumbnailLocationListener();
  const { isPatientMobile } = useViewerLayoutContext();
  const isHorizontalThumbnailList = thumbnailLocation === 'top' || isPatientMobile;

  const handleScroll = () => {
    const scrollContainer = scrollContainerRef_thumnailList.current;
    if (!scrollContainer) {
      return;
    }
    const { scrollWidth, clientWidth, scrollLeft } = scrollContainer;
    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft + clientWidth < scrollWidth - 6);
  };

  const onLoadComplete = () => {
    setIsLoaded(true);
  };

  useEffect(() => {
    const scrollContainer = scrollContainerRef_thumnailList.current;
    if (scrollContainer) {
      handleScroll();
      scrollContainer.addEventListener('scroll', handleScroll);
    }
    window.addEventListener('resize', handleScroll);

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
      window.removeEventListener('resize', handleScroll);
    };
  }, [thumbnails]);

  const scrollLeft = () => {
    const scrollContainer = scrollContainerRef_thumnailList.current;
    if (scrollContainer) {
      const newScrollLeft = scrollContainer.scrollLeft - 135;
      scrollContainer.scrollTo({
        left: Math.max(0, newScrollLeft),
        behavior: 'smooth',
      });
    }
  };

  const scrollRight = () => {
    const scrollContainer = scrollContainerRef_thumnailList.current;
    const { scrollWidth } = scrollContainer;
    if (scrollContainer) {
      const newScrollLeft = scrollContainer.scrollLeft + 135;
      scrollContainer.scrollTo({
        left: Math.min(newScrollLeft, scrollWidth + 1),
        behavior: 'smooth',
      });
    }
  };
  return (
    <div className={isPatientMobile ? 'relative h-[148px] w-full overflow-hidden' : ''}>
      {isPatientMobile && isLoaded && showLeftArrow && (
        <div
          className="absolute left-[1px] top-[4px] z-50 h-[140px] h-full w-[40px] cursor-pointer bg-black/[.7] text-center leading-[148px] leading-[135px] text-white"
          onClick={scrollLeft}
        >
          <LeftOutlined />
        </div>
      )}

      {/* <ScrollArea className="flex flex-col gap-[4px] pt-[4px] pr-[2.5px] pl-[5px] pb-[4px]"> */}
      <div
        ref={ref}
        className="flex flex-col gap-[4px] pt-[4px] pr-[2.5px] pl-[5px] pb-[4px]"
      >
        {thumbnailItems.length > 0 && (
          <div
            id="ohif-thumbnail-list"
            ref={scrollContainerRef_thumnailList}
            className={
              isHorizontalThumbnailList
                ? 'ohif-scrollbar bg-bkg-low flex touch-pan-x flex-row flex-nowrap place-items-start gap-[4px] overflow-x-auto overflow-y-hidden'
                : 'ohif-scrollbar bg-bkg-low grid grid-cols-[repeat(auto-fit,_minmax(0,135px))] place-items-start gap-[4px] overflow-y-hidden'
            }
          >
            {thumbnailItems.map(item => {
              const { displaySetInstanceUID, componentType, numInstances, ...rest } = item;
              const normalizedNumInstances = normalizeNumInstances(numInstances);

              const isActive = activeDisplaySetInstanceUIDs.includes(displaySetInstanceUID);
              return (
                <Thumbnail
                  servicesManager={servicesManager}
                  key={displaySetInstanceUID}
                  {...rest}
                  displaySetInstanceUID={displaySetInstanceUID}
                  numInstances={normalizedNumInstances}
                  isActive={isActive}
                  thumbnailType={componentType}
                  viewPreset="thumbnails"
                  onClick={onThumbnailClick.bind(null, displaySetInstanceUID)}
                  onDoubleClick={onThumbnailDoubleClick.bind(null, displaySetInstanceUID)}
                  onClickUntrack={onClickUntrack.bind(null, displaySetInstanceUID)}
                  ThumbnailMenuItems={ThumbnailMenuItems}
                  onLoadComplete={onLoadComplete}
                />
              );
            })}
          </div>
        )}
        {/* List Items */}
        {listItems.length > 0 && (
          <div
            id="ohif-thumbnail-list"
            className={
              isHorizontalThumbnailList
                ? 'ohif-scrollbar bg-bkg-low flex touch-pan-x flex-row flex-nowrap place-items-start gap-[4px] overflow-x-auto overflow-y-hidden'
                : 'ohif-scrollbar bg-bkg-low grid grid-cols-[repeat(auto-fit,_minmax(0,135px))] place-items-start gap-[4px] overflow-y-hidden'
            }
          >
            {listItems.map(item => {
              const { displaySetInstanceUID, componentType, numInstances, ...rest } = item;
              const normalizedNumInstances = normalizeNumInstances(numInstances);
              const isActive = activeDisplaySetInstanceUIDs.includes(displaySetInstanceUID);
              return (
                <Thumbnail
                  servicesManager={servicesManager}
                  key={displaySetInstanceUID}
                  {...rest}
                  displaySetInstanceUID={displaySetInstanceUID}
                  numInstances={normalizedNumInstances}
                  isActive={isActive}
                  thumbnailType={componentType}
                  viewPreset="list"
                  onClick={onThumbnailClick.bind(null, displaySetInstanceUID)}
                  onDoubleClick={onThumbnailDoubleClick.bind(null, displaySetInstanceUID)}
                  onClickUntrack={onClickUntrack.bind(null, displaySetInstanceUID)}
                  ThumbnailMenuItems={ThumbnailMenuItems}
                  onLoadComplete={onLoadComplete}
                />
              );
            })}
          </div>
        )}
      </div>
      {/* </ScrollArea> */}
      {isPatientMobile && isLoaded && showRightArrow && (
        <div
          className="absolute top-[4px] right-0 z-50 h-[140px] h-full w-[40px] cursor-pointer bg-black/[.7] text-center leading-[148px] leading-[135px] text-white"
          onClick={scrollRight}
        >
          <RightOutlined />
        </div>
      )}
    </div>
  );
};

export { ThumbnailList };
